import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import orderBy from 'lodash/orderBy'

import {
  LinkedDataAccount,
  LinkedDataConnectorItemEvents,
  LinkedDataAccountMappingData,
  LinkedDataConnectorItemEventType,
  LinkedDataManualLoader,
  LinkedDataPositionMappingData,
} from '@types'

import { prepareResponseError, updateStoreListItem } from '../utils/helpers'

import api from '@/store/api'
import { useRepositoriesStore } from '@/store/repositories/index'

export const useLinkedDataStore = defineStore('linked-data', () => {
  // INIT
  const repositoriesStore = useRepositoriesStore()

  const list = ref<LinkedDataAccount[]>([])

  const initFlag = ref(false)
  const loading = ref(true)
  const loadingEvents = ref(true)

  let abortController = new AbortController()

  const repositoryId = computed(() => repositoriesStore.getCurrentId)

  // GETTERS
  const getList = computed(() =>
    orderBy(list.value, item => item.account_name.toLowerCase(), 'asc'),
  )

  // SETTERS
  const setList = (data?: LinkedDataAccount[]) => {
    list.value = data || []
  }

  const setAccount = (account: LinkedDataAccount) => {
    updateStoreListItem(list.value, account)
  }

  // ACTIONS
  const fetch = async (): Promise<void> => {
    loading.value = true
    try {
      const result = await api.get<LinkedDataAccount[]>(
        'linked_data/accounts',
        {
          params: { repository_id: repositoryId.value, show_deleted: true },
          signal: abortController.signal,
        },
      )
      initFlag.value = true
      setList(result?.data)
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loading.value = false
    }
  }

  const fetchEvents = async (
    id: string,
    eventType?: LinkedDataConnectorItemEventType,
  ): Promise<LinkedDataConnectorItemEvents> => {
    try {
      loadingEvents.value = true
      const result = await api.get(`linked_data/accounts/${id}/sync_events`, {
        params: {
          repository_id: repositoryId.value,
          event_type: eventType,
        },
        signal: abortController.signal,
      })
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingEvents.value = false
    }
  }

  const fetchEvent = async (accountId: string, eventId: string): Promise<any> => {
    try {
      const result = await api.get(`linked_data/events/${eventId}`, {
        params: { repository_id: repositoryId.value, account_id: accountId },
        signal: abortController.signal,
      })
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const getAccount = async (id: string): Promise<LinkedDataAccount> => {
    try {
      const result = await api.get(`linked_data/accounts/${id}`, {
        params: { repository_id: repositoryId.value },
        signal: abortController.signal,
      })
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const getAccess = async (): Promise<{ token: string; url: string }> => {
    const { data } = await api.get('linked_data/access_token', {
      params: { repository_id: repositoryId.value },
      signal: abortController.signal,
    })
    return {
      token: data.token,
      url: data.url,
    }
  }

  const store = async (file_key: string, loader: LinkedDataManualLoader) => {
    try {
      const response = await api.post('linked_data/connectors/manual', {
        repository_id: repositoryId.value,
        file_key,
        loader,
      })
      return response.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const remapAccount = async (mappingData: LinkedDataAccountMappingData) => {
    try {
      const response = await api.put('linked_data/accounts/map', {
        ...mappingData,
        repository_id: repositoryId.value,
      })
      return response.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const remapPosition = async (mappingData: LinkedDataPositionMappingData) => {
    try {
      const response = await api.put('linked_data/positions/map', {
        ...mappingData,
        repository_id: repositoryId.value,
      })
      return response.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const callback = async (
    code: string,
    providerName: string,
    connectorName?: string,
  ) => {
    const params = [`code=${code}`]
    if (connectorName) {
      params.push(`connector=${connectorName}`)
    }
    try {
      const query = `${params.join('&')}${params.length > 0 ? '&' : ''}`
      const response = await api.get(
        `linked_data/callback/${providerName}?${query}repository_id=${repositoryId.value}`,
      )
      return response.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const sync = async () => {
    try {
      const response = await api.put('linked_data/accounts', null, {
        params: { repository_id: repositoryId.value },
      })
      return response.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const resync = async (connectorId: string) => {
    try {
      const response = await api.put(
        `linked_data/connectors/${connectorId}`,
        null,
        {
          params: { repository_id: repositoryId.value },
        },
      )
      return response.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const yodlee = async () => {
    try {
      const response = await api.put(
        'linked_data/accounts/repository_link',
        null,
        {
          params: {
            repository_id: repositoryId.value,
            access_method_type: 'yodlee',
          },
        },
      )
      return response.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const plaid = async (token: string) => {
    try {
      const response = await api.get('linked_data/callback/plaid', {
        params: { code: token, repository_id: repositoryId.value },
      })
      return response.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const arch = async (clientId: string, clientSecret: string) => {
    try {
      const response = await api.post('linked_data/arch_sync', {
        repository_id: repositoryId.value,
        client_id: clientId,
        client_secret: clientSecret,
      })
      return response.data?.task_id
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const cancel = () => {
    abortController.abort()
    abortController = new AbortController()
  }

  const clear = () => {
    initFlag.value = false

    loading.value = false
    loadingEvents.value = false

    list.value = []
  }

  return {
    initFlag,
    loading,
    loadingEvents,

    getList,

    setAccount,

    fetch,
    fetchEvents,
    fetchEvent,
    getAccount,
    getAccess,

    store,
    remapAccount,
    remapPosition,
    callback,
    sync,
    resync,
    yodlee,
    plaid,
    arch,

    cancel,
    clear,
  }
})
